/**
 * Constants shared inside the Input component.
 *
 * Separate from `input.tsx` because a module that exports both components and
 * plain values trips `react-refresh/only-export-components`. That is only a
 * warning here, but the registry is VENDORED — the file lands in a consumer's repo
 * as their own source, with instructions not to hand-edit it, so the warning
 * becomes permanent noise in their lint output that they cannot clear. Keeping
 * non-component exports in a sibling module costs nothing and fixes it for every
 * consumer at once.
 *
 * Re-exported from `index.ts`, because `TextareaField` needs it: it runs the same
 * press-redirect and must exclude the same interactive descendants. That makes it a
 * seam for sibling FIELD TYPES built on the Input family, not general consumer API —
 * application code composes the documented slots rather than matching against this
 * selector. `AGENTS.md` records it the same way.
 */

/**
 * Descendants that own their own click/focus behavior (clear button, info
 * trigger, a nested control). A press on one of these is never redirected to
 * the embedded control.
 */
export const FIELD_INTERACTIVE_SELECTOR = [
  // Natively interactive
  'a[href]',
  'area[href]',
  'button',
  'input',
  'select',
  'textarea',
  'label',
  'summary',
  'audio[controls]',
  'video[controls]',
  // Editable regions. `contenteditable="false"` is explicitly NOT interactive.
  '[contenteditable]:not([contenteditable="false"])',
  // Anything the author made focusable, including tabindex="-1" wrappers.
  '[tabindex]',
  // ARIA-only widgets: these own their own click behaviour but may carry no
  // tabindex, so the [tabindex] entry above does not catch them.
  '[role="button"]',
  '[role="link"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="tab"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="option"]',
  '[role="textbox"]',
  '[role="combobox"]',
  '[role="slider"]',
  '[role="spinbutton"]',
].join(', ');
